import { mediaDeletedEvent, mediaUpdatedEvent } from '@lib/events/eventBus';
import { isNotFoundError, isRateLimitError } from '@lib/network/api';
import { listLibraryFiles } from '@lib/network/libraryFolders';
import { isPollBackedOff, notePollRateLimited, notePollSucceeded } from '@lib/network/pollBackoff';
import { isBackendDrivenStatus } from '@pages/Library/utils/mediaStatus';
import { userStore } from '@stores/User';
import type { Media } from '@typings/Media';

/**
 * The single owner of media status/progress polling.
 *
 * ## Why this exists
 *
 * There used to be three independent pollers, all hitting
 * `GET /library/{id}/media/{mediaId}` — one request **per media**:
 *
 * - `Notifications.pollActiveMedia`, every 3s over every mediaId in a batch
 * - `useStatusPillRefresh`, a 500ms timer **per visible card**
 * - `usePeriodicMediaRefresh`, every 5s over every visible processing media
 *
 * Cost was linear in both batch size and visible-card count, uncapped, with no
 * 429 handling. A ~320-file upload produced **10,449 requests in one session**
 * and blew the backend's 10,000-per-10-minutes-per-user limit; the resulting
 * 429s were then read as "media deleted" and silently destroyed batch cards.
 *
 * ## The shape now
 *
 * Consumers `register()` a library they care about; this store owns the timers
 * and fans results out over the existing event buses (`mediaUpdatedEvent`,
 * `mediaDeletedEvent`) that `LibraryFolderStore` already subscribes to. Two
 * ticks, both **flat in batch size and card count**:
 *
 * - **Status** — one `GET /library/{id}/files` per registered library: every
 *   media in it, whichever library folder it is filed in. Carries every field the old
 *   per-media GET did (both are unprojected reads through the same
 *   `sanitizeMedia`), so nothing is lost by batching. Only the media whose
 *   state moved are emitted — see `selectChanged`.
 * - **Progress** — one `stats/processing` per library. This is the *only*
 *   source of `progress`: it is computed server-side by aggregation and
 *   `sanitizeMedia` strips the `completedTasks` it derives from, so no media
 *   endpoint carries it. Merged onto the media before emitting, which is why
 *   `getDisplayProgress` sees real values without any UI change.
 *
 * ## Absence is not deletion
 *
 * A media missing from a library listing is **not** pruned. It is confirmed by
 * a per-media GET (rate-limited by `CONFIRM_MISSING_*`) and only an explicit
 * 404 emits `mediaDeletedEvent`. The bug this replaces did the opposite —
 * `.catch(() => null)` treated every failure as a deletion, so one
 * rate-limited cycle deleted the user's batch card.
 */

/**
 * Status-transition sweep. One listing per registered library, so cost is
 * flat in batch size.
 *
 * The binding constraint is **payload, not request count**. 6 requests/min is
 * ~0.6% of the rate-limit budget, but `GET /library/{id}/files`
 * returns full unprojected media documents for the whole library with no
 * pagination — so on a large library each one is heavy, and the interval is
 * really a bandwidth knob.
 *
 * 10s is chosen against that: status only has to catch completed/errored
 * transitions (progress liveness comes from the stats tick below), and 10s is
 * imperceptible next to a processing job measured in minutes. The old
 * per-media poller ran at 3s because each request was small; that tradeoff
 * inverts once one request covers the whole library.
 *
 * This cannot be replaced by `stats/processing`: that endpoint matches
 * `status: 'processing'` and returns only `{_id, progress}`, so it cannot
 * distinguish `completed` from `errored`, and carries none of the fields a
 * finished card needs (`capabilities.preview`, duration, metadata). Note also
 * that the listing is uncached — it is the *fresher* of the two.
 */
const STATUS_POLL_INTERVAL_MS = 10_000;

/**
 * Server-side cache TTL for `GET /library/{id}/stats/processing`. Mirrors
 * `getCachedLibraryProcessingStats` in external.api.atlas — keep in sync.
 */
const PROCESSING_STATS_CACHE_TTL_MS = 30_000;

/**
 * Poll a beat past the server TTL so every request lands on an expired cache
 * and gets a freshly computed value, rather than spending a request on a
 * cached repeat. Polling *at* the TTL risks landing just before rollover,
 * which doubles worst-case staleness.
 *
 * Do not shorten this toward the route's 1-req-per-5s ceiling: that bucket is
 * keyed per (library, user), so a 5s tick is 100% of a sliding-window budget
 * and two tabs on the same library would 429 immediately.
 */
const PROCESSING_STATS_POLL_INTERVAL_MS = PROCESSING_STATS_CACHE_TTL_MS + 5_000;

/**
 * Minimum gap between `confirmMissing` checks for the same mediaId. Well above
 * the status interval so a durable mismatch costs one request a minute rather
 * than one per sweep.
 */
const CONFIRM_MISSING_COOLDOWN_MS = 60_000;

type ProcessingStatsResponse = {
  files: { _id: string; progress: number }[];
};

/**
 * Cap on per-media confirm-GETs per `confirmMissing` call.
 *
 * Without it, a batch whose media are durably absent from their library listing
 * bursts one request per media — the linear-in-batch-size shape this class
 * exists to remove, reachable with no bug at all. A durable mismatch now
 * drains a few at a time instead.
 */
const CONFIRM_MISSING_MAX_PER_SWEEP = 5;

/**
 * Consecutive zero-change sweeps before a library is parked.
 *
 * The backstop for "a media stuck in a pre-finish state polls forever", which
 * has recurred twice: `empty` orphans, and a `'success'` upload entry whose
 * media never leaves `empty` (since `clearUploadForMedia` only runs once the
 * media is past `empty`, the two wait on each other). Bounding on *observed
 * change* covers both without enumerating statuses.
 *
 * It deliberately does **not** cover a dead `queued`/`processing` job: idle
 * only accrues while nothing in the library is backend-driven, because those
 * states can legitimately hold one signature for many minutes. See the guard
 * at the call site.
 *
 * 30 × 10s ≈ 5 minutes. Parking is lifted whenever registrations change (a view
 * remount, a new batch) and on tab focus, so a revived library is picked back up.
 */
const MAX_IDLE_SWEEPS = 30;

export class MediaStatusPoller {
  /** Refcount per registered libraryId. */
  private registrations = new Map<string, number>();

  private statusTimer: ReturnType<typeof setInterval> | null = null;
  private statsTimer: ReturnType<typeof setInterval> | null = null;

  /** Latest known 0..1 progress per mediaId, from the stats endpoint. */
  private progressByMediaId = new Map<string, number>();

  /**
   * Last emitted signature per mediaId, so a sweep can emit only what changed.
   *
   * This is what keeps a no-change tick free. `mediaUpdatedEvent` is a *delta*
   * channel — `LibraryFolderStore` responds by rebuilding `data.files`, which
   * replaces every entry's object identity and so re-renders every memoized
   * card and invalidates every count/sort computed on the store. Emitting the
   * whole listing unconditionally made that fire every tick for a folder where
   * nothing had moved.
   */
  private lastEmittedSignature = new Map<string, string>();

  /**
   * Latest full listing state per mediaId, across every watched library.
   *
   * `mediaUpdatedEvent` carries only what *changed*, which is right for the
   * render path but useless to a consumer that needs to reason about a whole
   * batch — "not in this emission" means "unchanged", not "gone". Consumers ask
   * here instead (see `getSnapshot`). Without it, `Notifications` read absence
   * as absence and batches never completed.
   */
  private snapshotByMediaId = new Map<string, Media>();

  /** Which sweeps are in flight, so a slow cycle can't overlap its successor. */
  private inFlight = new Set<'status' | 'stats'>();

  /** When the last status sweep ran, to throttle out-of-band refreshes. */
  private lastStatusSweepAt = 0;

  /**
   * Targets whose last request failed, so a sustained outage warns **once**
   * rather than once per sweep.
   *
   * A poller warning on every failure would emit a line every 10s for the
   * length of an outage. `captureConsoleIntegration` only promotes
   * `console.error`, so this was never Sentry noise — but it would bury the
   * console, and the convention here is that surviving `console.warn`
   * diagnostics stay low-volume.
   */
  private failingTargets = new Set<string>();

  /**
   * Consecutive zero-change sweeps per library. A library that reaches
   * `MAX_IDLE_SWEEPS` is parked — see that constant for why this is bounded on
   * observed change rather than on status.
   */
  private idleSweeps = new Map<string, number>();

  /**
   * When each mediaId was last checked by `confirmMissing`, so a persistent
   * mismatch cannot reintroduce per-media polling.
   *
   * Without this, any media that is durably absent from its library listing
   * would be re-confirmed on every single status sweep. That is exactly the per-media-request-per-cycle shape
   * this class exists to remove, so the cooldown is load-bearing, not tidiness.
   */
  private lastConfirmedAt = new Map<string, number>();

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  /**
   * Declare interest in a library's media status. Refcounted, so the upload
   * panel and the library grid can both ask for the same library independently.
   * Returns the disposer — call it on unmount / when the batch finishes.
   */
  register(libraryId: string): () => void {
    this.registrations.set(libraryId, (this.registrations.get(libraryId) ?? 0) + 1);
    // A fresh registration is new evidence that someone expects movement here,
    // so lift any parking. Without this a library parked during one batch would
    // stay parked for the next one.
    this.idleSweeps.delete(libraryId);
    this.syncTimers();

    let released = false;
    return () => {
      // Guard double-release: a disposer called twice would decrement someone
      // else's refCount and stop polling while they still care.
      if (released) return;
      released = true;
      const refCount = (this.registrations.get(libraryId) ?? 0) - 1;
      if (refCount > 0) this.registrations.set(libraryId, refCount);
      else this.registrations.delete(libraryId);
      this.syncTimers();
    };
  }

  /**
   * Resolve a set of mediaIds against the latest library listings.
   *
   * The counterpart to delta emission: `mediaUpdatedEvent` says *what moved*,
   * this says *what is*. A consumer reasoning about a whole batch — "are all of
   * these done?", "which of these errored?" — must use this, because an id
   * absent from an emission is almost always just unchanged.
   *
   * `absent` means "not in any listing we have fetched", which is the only
   * input that justifies a `confirmMissing` check.
   */
  getSnapshot(mediaIds: string[]): { found: Media[]; absent: string[] } {
    const found: Media[] = [];
    const absent: string[] = [];
    for (const mediaId of mediaIds) {
      const media = this.snapshotByMediaId.get(mediaId);
      if (media) found.push(media);
      else absent.push(mediaId);
    }
    return { found, absent };
  }

  /**
   * Sweep now rather than waiting for the next tick — for the moment a batch
   * starts or the tab regains focus, where a full interval of stale state
   * reads as a hang.
   *
   * Throttled to one interval: this fires on every tab focus and every
   * registration change, so unthrottled it would let alt-tabbing repeatedly
   * pull the (unprojected, whole-library) listing as fast as the user can
   * switch windows.
   */
  refreshNow(): void {
    if (Date.now() - this.lastStatusSweepAt < STATUS_POLL_INTERVAL_MS) return;
    void this.sweepStatus();
    void this.sweepProcessingStats();
  }

  private handleVisibilityChange = () => {
    // A hidden tab polled forever before this existed. Resume with an
    // immediate sweep so coming back to the tab doesn't show stale state for
    // an interval. `refreshNow` and both sweeps already gate on `shouldPoll`,
    // so no condition is needed here.
    this.syncTimers();
    if (!document.hidden) {
      // Returning to the tab is fresh evidence the user wants current state, so
      // give parked libraries one more look rather than leaving them parked for
      // the rest of the session.
      this.idleSweeps.clear();
    }
    this.refreshNow();
  };

  /** Warn on the transition into failure only; stay quiet while it persists. */
  private noteFailure(target: string, message: string, e: unknown): void {
    if (this.failingTargets.has(target)) return;
    this.failingTargets.add(target);
    console.warn(message, e);
  }

  /** Clear a target's failure state so a later outage warns again. */
  private noteHealthy(target: string): void {
    this.failingTargets.delete(target);
  }

  /**
   * Note a 429 and tell the caller to abandon the rest of the sweep.
   *
   * Returns false for anything else, so the caller can fall through to its own
   * handling.
   */
  private noteRateLimit(e: unknown): boolean {
    if (!isRateLimitError(e)) return false;
    notePollRateLimited();
    return true;
  }

  /**
   * Shared preamble for both sweeps: the "am I allowed to poll right now?"
   * rule lives here once, as does the overlap guard.
   */
  private async runSweep(kind: 'status' | 'stats', body: () => Promise<void>): Promise<void> {
    if (this.inFlight.has(kind) || !this.shouldPoll || isPollBackedOff()) return;
    this.inFlight.add(kind);
    try {
      await body();
    } finally {
      this.inFlight.delete(kind);
    }
  }

  private get shouldPoll(): boolean {
    if (this.registrations.size === 0) return false;
    if (typeof document !== 'undefined' && document.hidden) return false;
    return true;
  }

  private syncTimers() {
    if (this.shouldPoll) {
      if (!this.statusTimer) {
        this.statusTimer = setInterval(() => void this.sweepStatus(), STATUS_POLL_INTERVAL_MS);
      }
      if (!this.statsTimer) {
        this.statsTimer = setInterval(
          () => void this.sweepProcessingStats(),
          PROCESSING_STATS_POLL_INTERVAL_MS,
        );
      }
      return;
    }
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
    if (this.statsTimer) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
    // Only once nothing is registered at all — not merely because the tab went
    // hidden. Drops the per-media bookkeeping, which would otherwise grow
    // monotonically across a long session of uploads.
    if (this.registrations.size === 0) {
      this.progressByMediaId.clear();
      this.lastConfirmedAt.clear();
      this.lastEmittedSignature.clear();
      this.snapshotByMediaId.clear();
      this.idleSweeps.clear();
      this.failingTargets.clear();
    }
  }

  /**
   * One listing per registered library, emitting only what changed.
   */
  private sweepStatus(): Promise<void> {
    return this.runSweep('status', async () => {
      this.lastStatusSweepAt = Date.now();
      for (const libraryId of [...this.registrations.keys()]) {
        // Parked: nothing in this library has changed for MAX_IDLE_SWEEPS, so
        // whatever is still nominally in flight cannot advance on its own.
        if ((this.idleSweeps.get(libraryId) ?? 0) >= MAX_IDLE_SWEEPS) continue;
        try {
          const data = await listLibraryFiles(libraryId);
          notePollSucceeded();
          this.noteHealthy(libraryId);
          // Merge real progress once, then use the merged records for both the
          // snapshot and the delta. The snapshot must hold merged media:
          // consumers compute `getDisplayProgress` off it, and raw listing
          // records carry no `progress` at all.
          const merged = (data?.files ?? []).map((file) => this.withMergedProgress(file));
          for (const media of merged) this.snapshotByMediaId.set(media._id, media);
          const changed = this.selectChanged(merged);

          // Only accrue idleness when nothing here is backend-driven.
          //
          // `progress` is a ratio of completed workers, so a `processing` media
          // can hold one value for many minutes during a single long stage —
          // and observed sweeps confirm the signature is unchanged between
          // 35s stats ticks. Counting those as idle would park a library whose
          // media is alive and then miss its completion. False-park costs a
          // visibly stuck card; over-polling a genuinely dead backend job costs
          // ~6 req/min, so the asymmetry says be patient here.
          const hasBackendWork = merged.some((m) => isBackendDrivenStatus(m.status));
          const idle =
            changed.length === 0 && !hasBackendWork ? (this.idleSweeps.get(libraryId) ?? 0) + 1 : 0;
          this.idleSweeps.set(libraryId, idle);
          // Emitting nothing on a no-change tick is the point: every subscriber
          // treats an emission as "these moved", and LibraryFolderStore reacts
          // by rebuilding `data.files` — which costs a full re-render.
          if (changed.length > 0) mediaUpdatedEvent.emit({ libraryId, media: changed });
        } catch (e) {
          if (this.noteRateLimit(e)) return;
          // Anything else: skip this library for this cycle. Never treat a
          // failed listing as evidence about the media it would have listed.
          this.noteFailure(libraryId, `status sweep failed for library ${libraryId}`, e);
        }
      }
    });
  }

  /**
   * Reduce already-merged media to those whose displayed state actually moved.
   *
   * The signature covers exactly what consumers render off: status, progress,
   * and the two `capabilities` booleans that decide whether a card can show a
   * thumbnail. A media whose signature is unchanged is dropped, so a steady
   * library emits nothing at all.
   */
  private selectChanged(mergedMedia: Media[]): Media[] {
    const changed: Media[] = [];
    for (const media of mergedMedia) {
      const signature = [
        media.status,
        media.progress ?? '',
        media.capabilities?.preview ? 1 : 0,
        media.capabilities?.search ? 1 : 0,
      ].join('|');
      if (this.lastEmittedSignature.get(media._id) === signature) continue;
      this.lastEmittedSignature.set(media._id, signature);
      changed.push(media);
    }
    return changed;
  }

  /**
   * One request per distinct library. Returns `{_id, progress}` for every
   * media in `processing` — the only place real progress is available.
   */
  private sweepProcessingStats(): Promise<void> {
    return this.runSweep('stats', async () => {
      for (const libraryId of [...this.registrations.keys()]) {
        const path = `/library/${libraryId}/stats/processing`;
        try {
          const stats = await userStore.apiWithAuth<ProcessingStatsResponse>(path);
          notePollSucceeded();
          this.noteHealthy(path);
          const entries = stats?.files ?? [];
          for (const entry of entries) {
            if (typeof entry?.progress === 'number' && entry._id) {
              this.progressByMediaId.set(entry._id, entry.progress);
            }
          }
        } catch (e) {
          if (this.noteRateLimit(e)) return;
          this.noteFailure(path, `processing stats failed for library ${libraryId}`, e);
        }
      }
    });
  }

  /**
   * Attach the last known real progress. Media that have left `processing`
   * keep no stale value — their progress is meaningless once terminal, and a
   * lingering one would make a completed card read as partially done.
   */
  private withMergedProgress(media: Media): Media {
    if (media.status !== 'processing') {
      this.progressByMediaId.delete(media._id);
      return media;
    }
    const progress = this.progressByMediaId.get(media._id);
    return progress === undefined ? media : { ...media, progress };
  }

  /**
   * Confirm whether media absent from a library listing are really gone, and
   * emit `mediaDeletedEvent` for the ones that 404.
   *
   * Callers pass only IDs they expected to see and didn't, so this is rare —
   * which is what keeps the library-listing win intact. Absence alone is never
   * enough: a media can be missing because the listing raced a write, and
   * acting on that is the bug this whole change exists to remove.
   */
  async confirmMissing(libraryId: string, mediaIds: string[]): Promise<string[]> {
    const gone: string[] = [];
    const now = Date.now();
    // Oldest-checked first, so a capped sweep still works through the whole
    // set over successive cycles instead of re-checking the same head.
    const due = mediaIds
      .filter((id) => now - (this.lastConfirmedAt.get(id) ?? 0) >= CONFIRM_MISSING_COOLDOWN_MS)
      .sort((a, b) => (this.lastConfirmedAt.get(a) ?? 0) - (this.lastConfirmedAt.get(b) ?? 0))
      .slice(0, CONFIRM_MISSING_MAX_PER_SWEEP);

    for (const mediaId of due) {
      if (isPollBackedOff()) break;
      this.lastConfirmedAt.set(mediaId, now);
      const path = `/library/${libraryId}/media/${mediaId}`;
      try {
        // Reachable but absent from the listing: the listing raced a write.
        // Leave it alone rather than prune something that demonstrably exists.
        await userStore.apiWithAuth<Media>(path);
        notePollSucceeded();
      } catch (e) {
        if (isNotFoundError(e)) {
          gone.push(mediaId);
          continue;
        }
        if (this.noteRateLimit(e)) break;
        // Unreachable for any other reason — leave it alone.
      }
    }
    if (gone.length > 0) mediaDeletedEvent.emit({ libraryId, mediaIds: gone });
    return gone;
  }
}

export const mediaStatusPoller = new MediaStatusPoller();
